import { useState, useCallback, useEffect, useRef } from 'react';
import type { IntakeResponse } from '../services/api';
import type { IntakeRecord } from '../store/slices/intakeSlice';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseIntakeFieldSaveOptions {
  intakeId: string | undefined;
  updateIntakeRecord: (
    id: string,
    data: Partial<IntakeResponse>
  ) => Promise<IntakeRecord>;
  /** Called after a successful save (e.g. to reload relationships). */
  onAfterSave?: () => Promise<void> | void;
}

interface UseIntakeFieldSaveReturn {
  saveStatus: SaveStatus;
  /** Save a single field immediately. */
  saveField: (fieldName: string, value: unknown) => Promise<void>;
  /** Save multiple fields immediately. */
  saveBatch: (updates: Record<string, unknown>) => Promise<void>;
  /** Schedule a debounced save for a field. */
  debouncedSave: (fieldName: string, value: unknown, delay?: number) => void;
  /** Cancel a pending debounced save for a field. */
  cancelDebounce: (fieldName: string) => void;
}

/**
 * Manages save-status indicators and debounced/immediate field saves for
 * intake forms. Replaces the duplicated saveStatus + saveTimeoutRef +
 * debounceTimeoutsRef boilerplate in EligibilityCheckStep, PriorAuthStep,
 * DynamicIntakeForm, etc.
 */
export function useIntakeFieldSave({
  intakeId,
  updateIntakeRecord,
  onAfterSave,
}: UseIntakeFieldSaveOptions): UseIntakeFieldSaveReturn {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const debounceTimeoutsRef = useRef<Record<string, NodeJS.Timeout>>({});

  // Keep onAfterSave in a ref so it doesn't cause re-creation of callbacks.
  const onAfterSaveRef = useRef(onAfterSave);
  onAfterSaveRef.current = onAfterSave;

  // Cancel pending debounced saves when the intake changes so a queued
  // save from the previous intake never fires against the new one.
  useEffect(() => {
    Object.values(debounceTimeoutsRef.current).forEach(clearTimeout);
    debounceTimeoutsRef.current = {};

    return () => {
      Object.values(debounceTimeoutsRef.current).forEach(clearTimeout);
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [intakeId]);

  const setSaved = useCallback(() => {
    setSaveStatus('saved');
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
  }, []);

  const setError = useCallback(() => {
    setSaveStatus('error');
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => setSaveStatus('idle'), 3000);
  }, []);

  const saveBatch = useCallback(
    async (updates: Record<string, unknown>) => {
      if (!intakeId) return;
      try {
        setSaveStatus('saving');
        await updateIntakeRecord(intakeId, updates as Partial<IntakeResponse>);
        if (onAfterSaveRef.current) await onAfterSaveRef.current();
        setSaved();
      } catch (err) {
        console.error('Failed to save:', err);
        setError();
      }
    },
    [intakeId, updateIntakeRecord, setSaved, setError]
  );

  const saveField = useCallback(
    async (fieldName: string, value: unknown) => {
      await saveBatch({ [fieldName]: value });
    },
    [saveBatch]
  );

  const debouncedSave = useCallback(
    (fieldName: string, value: unknown, delay = 1500) => {
      if (debounceTimeoutsRef.current[fieldName]) {
        clearTimeout(debounceTimeoutsRef.current[fieldName]);
      }
      debounceTimeoutsRef.current[fieldName] = setTimeout(() => {
        void saveField(fieldName, value);
        delete debounceTimeoutsRef.current[fieldName];
      }, delay);
    },
    [saveField]
  );

  const cancelDebounce = useCallback((fieldName: string) => {
    if (debounceTimeoutsRef.current[fieldName]) {
      clearTimeout(debounceTimeoutsRef.current[fieldName]);
      delete debounceTimeoutsRef.current[fieldName];
    }
  }, []);

  return { saveStatus, saveField, saveBatch, debouncedSave, cancelDebounce };
}
